const MS_PER_SECOND = 1000;

/**
 * @param seconds Number of seconds (fractional values allowed).
 * @returns Equivalent duration in milliseconds.
 */
export function getSecondsInMilliseconds(seconds: number): number {
  return seconds * MS_PER_SECOND;
}

/**
 * @param hours Number of hours (fractional values allowed, e.g. `1.5` for 90 minutes).
 * @returns Equivalent duration in milliseconds.
 */
export function getHoursInMilliseconds(hours: number): number {
  return getSecondsInMilliseconds(hours * 3600);
}

/**
 * @param days Number of days (e.g. `1` for 24 hours). Wall-clock duration, not calendar-aware.
 * @returns Equivalent duration in milliseconds.
 */
export function getDaysInMilliseconds(days: number): number {
  return getHoursInMilliseconds(days * 24);
}

/**
 * Checks if the current time falls within a happy hour time range
 * @param happyHourStr - Time range string in format "4pm-6pm" or "4:30pm-6:30pm"
 * @returns boolean indicating if current time is within happy hour
 */
export function isCurrentlyHappyHour(happyHourStr: string): boolean {
  if (!happyHourStr || happyHourStr.toLowerCase() === "n/a") {
    return false;
  }

  const [startTime, endTime] = happyHourStr
    .toLowerCase()
    .split("-")
    .map((t) => t.trim());
  if (!startTime || !endTime) return false;

  const now = new Date();
  const currentTimeInMinutes = now.getHours() * 60 + now.getMinutes();

  const parseTime = (timeStr: string): number => {
    const match = timeStr.match(/(\d+)(?::(\d+))?\s*(am|pm)/);
    if (!match) return -1;

    let [_, hourStr, minuteStr = "0", period] = match;
    let hour = parseInt(hourStr, 10);
    const minute = parseInt(minuteStr, 10);

    if (period === "pm" && hour !== 12) hour += 12;
    if (period === "am" && hour === 12) hour = 0;

    return hour * 60 + minute;
  };

  const startMinutes = parseTime(startTime);
  const endMinutes = parseTime(endTime);

  if (startMinutes === -1 || endMinutes === -1) return false;

  // Handle ranges that span past midnight
  if (endMinutes < startMinutes) {
    return (
      currentTimeInMinutes >= startMinutes || currentTimeInMinutes <= endMinutes
    );
  }

  return (
    currentTimeInMinutes >= startMinutes && currentTimeInMinutes <= endMinutes
  );
}

/**
 * Convert Unix timestamp to readable time string (HH:MM format)
 * @param timestamp Unix timestamp in seconds
 * @returns Time string in HH:MM format
 */
export const unixTimestampToTimeString = (timestamp: number): string => {
  if (!timestamp) return "";

  const date = new Date(timestamp * 1000); // Convert seconds to milliseconds
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  return `${hours}:${minutes}`;
};

/**
 * Convert Unix timestamp to readable time string with AM/PM format
 * @param timestamp Unix timestamp in seconds
 * @returns Time string in 12-hour format (e.g., "2:30 PM")
 */
export const unixTimestampTo12HourTime = (timestamp: number): string => {
  if (!timestamp) return "";

  const date = new Date(timestamp * 1000);
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

/**
 * Convert time string to Unix timestamp for today
 * @param timeStr Time string in format "HH:MM" or "H:MM"
 * @returns Unix timestamp in seconds
 */
export const timeStringToUnixTimestamp = (timeStr: string): number => {
  if (!timeStr) return 0;

  const [hours, minutes] = timeStr.split(":").map(Number);
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return Math.floor(date.getTime() / 1000);
};

/**
 * Check if current time is within a time range (Unix timestamps)
 * @param startTimestamp Start time as Unix timestamp
 * @param endTimestamp End time as Unix timestamp
 * @returns boolean indicating if current time is within range
 */
export const isTimeWithinRange = (
  startTimestamp: number,
  endTimestamp: number
): boolean => {
  if (!startTimestamp || !endTimestamp) return true;

  const now = new Date();
  const currentTimestamp = Math.floor(now.getTime() / 1000);

  // Handle cases where the time window spans midnight
  if (startTimestamp <= endTimestamp) {
    // Normal case: start time is before end time
    return (
      currentTimestamp >= startTimestamp && currentTimestamp <= endTimestamp
    );
  } else {
    // Midnight spanning case: start time is after end time
    return (
      currentTimestamp >= startTimestamp || currentTimestamp <= endTimestamp
    );
  }
};

/**
 * Get current Unix timestamp
 * @returns Current Unix timestamp in seconds
 */
export const getCurrentUnixTimestamp = (): number => {
  return Math.floor(Date.now() / 1000);
};

/**
 * Create a Unix timestamp for a specific time today
 * @param hours Hour (0-23)
 * @param minutes Minute (0-59)
 * @returns Unix timestamp in seconds
 */
export const createTimestampForTime = (
  hours: number,
  minutes: number
): number => {
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return Math.floor(date.getTime() / 1000);
};

/**
 * Create a Unix timestamp for a specific time on a specific date
 * @param date Date object
 * @param hours Hour (0-23)
 * @param minutes Minute (0-59)
 * @returns Unix timestamp in seconds
 */
export const createTimestampForDateTime = (
  date: Date,
  hours: number,
  minutes: number
): number => {
  const newDate = new Date(date);
  newDate.setHours(hours, minutes, 0, 0);
  return Math.floor(newDate.getTime() / 1000);
};

/**
 * Validate that a Unix timestamp is reasonable (not in the past for today's times)
 * @param timestamp Unix timestamp in seconds
 * @returns boolean indicating if timestamp is valid
 */
export const isValidTimestamp = (timestamp: number): boolean => {
  if (!timestamp || timestamp <= 0) return false;

  const now = Math.floor(Date.now() / 1000);
  const todayStart = Math.floor(new Date().setHours(0, 0, 0, 0) / 1000);

  // Timestamp should be for today or future
  return timestamp >= todayStart;
};
